// src/pages/proyectos/ProyectosPage.tsx
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { QDataTable } from '@/components/tables';
import { Plus, Calendar, Clock, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useProyectos } from '@/hooks/queries/useProyectos';
import { useScreenState } from '@/hooks/useScreenState';
import { buildTableProps } from '@/lib/screen-utils';
import { proyectoScreenDef } from '@/screen-definitions';
import { ProyectoFormModal } from './components/ProyectoFormModal';
import type { ProyectoListItem, ProyectoFiltros, ProjectStatus } from '@/types';

export function ProyectosPage() {
  const navigate = useNavigate();
  const screenState = useScreenState(proyectoScreenDef);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Build entity-specific query params from screen state
  const queryFiltros = useMemo<ProyectoFiltros>(() => {
    const estados = screenState.quickFilterValues.estado;
    const fecha = screenState.filterValues.fecha as { from?: string; to?: string } | undefined;
    return {
      estado: estados?.length === 1 ? estados[0] as ProjectStatus : undefined,
      fecha_desde: fecha?.from,
      fecha_hasta: fecha?.to,
      solo_activos: true,
    };
  }, [screenState.quickFilterValues, screenState.filterValues]);

  const query = useProyectos(queryFiltros);

  // Client-side search: backend doesn't support search param for proyectos
  const filteredQuery = useMemo(() => {
    const search = screenState.search?.toLowerCase();
    if (!search || !query.data) return query;
    const filtered = (query.data as ProyectoListItem[]).filter((p) =>
      (p.nombre ?? '').toLowerCase().includes(search) ||
      (p.codigo ?? '').toLowerCase().includes(search)
    );
    return { ...query, data: filtered };
  }, [query, screenState.search]);

  const tableProps = buildTableProps(proyectoScreenDef, screenState, filteredQuery, {
    getId: (p: ProyectoListItem) => p.id,
    onRowClick: (p: ProyectoListItem) => navigate(`/proyectos/${p.id}`),
    customRenderers: {
      nombre: (row: ProyectoListItem) => (
        <div>
          <div className="font-medium">{row.nombre}</div>
          <div className="text-sm text-muted-foreground">{row.codigo}</div>
        </div>
      ),
      cliente_nombre: (row: ProyectoListItem) =>
        row.cliente_nombre ? (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {row.cliente_nombre}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Sin cliente</span>
        ),
      fecha_inicio_planeada: (row: ProyectoListItem) => (
        <div className="text-sm space-y-1">
          {row.fecha_inicio_planeada && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {format(new Date(row.fecha_inicio_planeada), 'dd MMM yyyy', { locale: es })}
            </div>
          )}
          {row.fecha_fin_planeada && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {format(new Date(row.fecha_fin_planeada), 'dd MMM yyyy', { locale: es })}
            </div>
          )}
        </div>
      ),
      progreso_porcentaje: (row: ProyectoListItem) => (
        <div className="w-24">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{row.progreso_porcentaje.toFixed(0)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${row.progreso_porcentaje}%` }}
            />
          </div>
        </div>
      ),
      horas_registradas: (row: ProyectoListItem) => (
        <div className="flex items-center gap-1 text-sm">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span>{row.horas_registradas}</span>
          {row.horas_estimadas && (
            <span className="text-muted-foreground">/ {row.horas_estimadas}</span>
          )}
        </div>
      ),
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Proyectos</h1>
          <p className="text-muted-foreground">Gestiona proyectos, fases y sprints</p>
        </div>
      </div>

      <QDataTable
        {...tableProps}
        headerActions={
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Proyecto
          </Button>
        }
      />

      <ProyectoFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
